import { ILoadWeatherReportResponse } from '../report/server/load/load-report';
import _ from 'lodash';

// Helper function to get a random number between min and max
function getRandomNumber(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

// Helper function to get a random integer between min and max (inclusive)
function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Helper function to round a number to a specific precision
function roundTo(num: number, precision: number): number {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
}

// Generate a date number in YYYYMMDD format
function generateDateNumber(date: Date): IDateNumber {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return parseInt(`${year}${month}${day}`);
}

// Get MS since epoch for a specific date and time
function getMsSinceEpoch(date: Date): number {
  return date.getTime();
}

// Function to create IWeatherReportNextHour data
function createWeatherReportNextHour(): IWeatherReportNextHour {
  // Today should be rainy as specified
  const minutes: IWeatherReportNextHourMinute[] = [];

  for (const [i, h] of [
    1, 1, 1, 1, 1, 1, 1, 1, 0.95, 0.92,
    0.7014, 0.6914, 0.6814, 0.6733, 0.6673, 0.6593, 0.6553, 0.6493, 0.6433, 0.6293,
    0.6112, 0.5932, 0.5591, 0.515, 0.4649, 0.4088, 0.3527, 0.3086, 0.2705, 0.2365,
    0.2104, 0.1864, 0.1663, 0.1523, 0.1403, 0.1303, 0.1242, 0.1182, 0.1122, 0.1102,
    0.1062, 0.1062, 0.1062, 0.1062, 0.1082, 0.1122, 0.1162, 0.1222, 0.1303, 0.1423,
    0.1583, 0.1784, 0.2064, 0.2485, 0.3006, 0.3888, 0.523, 0.6012, 0.6593, 0.6914
  ].entries()) {
    const chance = (1 - h);
    const intensity = chance;
    minutes.push({
      time: Date.now() + (1000*60*i),
      chance: roundTo(chance, 4),
      intensity: roundTo(intensity, 4)
    });
  }

  return {
    precipitationType: 'rain', // Rainy as requested
    minutes
  };
}

// Function to create IWeatherReportDayHour data
function createWeatherReportDayHour(
  date: Date,
  temp: number,
  percRain: number
): IWeatherReportDayHour {
  const precipChance = percRain;
  const precipAmount = percRain;
  const precipType = 'rain';
  const tempReal = temp;
  const windSpeed = getRandomNumber(5, 20);
  const tempFeel = roundTo(tempReal - (windSpeed > 15 ? 1 : 0), 1);

  return {
    time: getMsSinceEpoch(date),
    tempReal,
    tempFeel,
    precipChance: roundTo(precipChance, 2),
    precipAmount: roundTo(precipAmount, 2),
    precipType,
    windSpeed: roundTo(windSpeed, 1)
  };
}

// Function to create IWeatherReportPollen data
function createWeatherReportPollen(): IWeatherReportPollen[] {
  const pollenTypes = ['Tree', 'Grass', 'Weed', 'Mold'];

  return pollenTypes.map(type => ({
    title: type,
    level: getRandomInt(0, 5) as 0 | 1 | 2 | 3 | 4 | 5
  }));
}

// Function to create IWeatherReportDay data
function createWeatherReportDay(
  date: Date,
  daysFromToday: number,
  isRainyDay: boolean = false
): IWeatherReportDay {
  const dateNumber = generateDateNumber(date);
  const hours: IWeatherReportDayHour[] = [];

  // Generate hourly data for the entire day
  const temps = (() => {
    if (!daysFromToday) return [0.024, 0.026, 0.027, 0.031, 0.043, 0.067, 0.114, 0.177, 0.247, 0.323, 0.415, 0.521, 0.622, 0.714, 0.79, 0.851, 0.897, 0.93, 0.953, 0.965, 0.968, 0.968, 0.965, 0.96].map(x => 18 + (x * 18));
    return [0.1081, 0.1342, 0.1718, 0.2211, 0.282, 0.3607, 0.4563, 0.5558, 0.6407, 0.687, 0.7252, 0.7563, 0.78, 0.7981, 0.8107, 0.8172, 0.8168, 0.8104, 0.7968, 0.7729, 0.735, 0.6873, 0.6705, 0.6638].map(x => 14 + (x * 16));
  })();
  const rains = [0.752, 0.77, 0.786, 0.8, 0.808, 0.81, 0.804, 0.794, 0.778, 0.756, 0.724, 0.678, 0.606, 0.482, 0.288, 0.216, 0.182, 0.162, 0.148, 0.134, 0.124, 0.114, 0.104, 0.096];
  for (const [i, r] of rains.entries()) {
    const hourDate = new Date(date);
    hourDate.setHours(i);
    const percRain = r;
    const temp = temps[i];
    hours.push(createWeatherReportDayHour(hourDate, temp, percRain));
  }

  // Calculate temperature high and low from the hourly data
  let tempHigh = Math.max(...hours.map(h => h.tempReal));
  let tempLow = Math.min(...hours.map(h => h.tempReal));
  let precipChance = isRainyDay ? getRandomNumber(0.6, 0.9) : getRandomNumber(0, 0.3);
  let windSpeed = roundTo(getRandomNumber(8, 15), 1);
  const windMaxSpeed = roundTo(windSpeed + getRandomNumber(5, 15), 1);
  const windDirection = getRandomInt(0, 360);
  const sunriseDate = new Date(date);
  sunriseDate.setHours(6, getRandomInt(15, 45), 0);
  const sunsetDate = new Date(date);
  sunsetDate.setHours(20, getRandomInt(0, 30), 0);

  // Determine condition code
  let conditionCode: IConditionCode = 'Clear';
  if (isRainyDay) {
    conditionCode = Math.random() < 0.3 ? 'Thunderstorms' : 'Rain';
  } else {
    conditionCode = 'Clear';
  }

  const futureWeekDays = [
    { low: 23, high: 33, rain: 0.0, conditionCode: <IConditionCode>'PartlyCloudy' },
    { low: 24, high: 35, rain: 0.0, conditionCode: <IConditionCode>'Clear' },
    { low: 20, high: 32, rain: 0.0, conditionCode: <IConditionCode>'Windy' },
    { low: 16, high: 29, rain: 0.3, conditionCode: <IConditionCode>'Cloudy' },
    { low: 16, high: 27, rain: 0.6, conditionCode: <IConditionCode>'Rain' },
  ] as const;
  const fd = daysFromToday > 0 ? futureWeekDays[daysFromToday-1] : null;
  if (fd) {
    tempHigh = fd.high;
    tempLow = fd.low;
    conditionCode = fd.conditionCode;
    precipChance = fd.rain;
  }

  return {
    dateNumber,
    daysFromToday,
    conditionCode,
    hours,
    tempHigh: roundTo(tempHigh, 1),
    tempLow: roundTo(tempLow, 1),
    precipChance: roundTo(precipChance, 2),
    precipType: precipChance > 0.2 ? 'rain' : 'clear',
    windSpeed,
    windMaxSpeed,
    windDirection,
    sunriseTime: getMsSinceEpoch(sunriseDate),
    sunsetTime: getMsSinceEpoch(sunsetDate),
    moonPhase: roundTo(Math.random(), 2),
    humidity: roundTo(isRainyDay ? getRandomNumber(0.7, 0.9) : getRandomNumber(0.4, 0.7), 2),
    tempDewPoint: roundTo(isRainyDay ? getRandomNumber(12, 16) : getRandomNumber(8, 14), 1),
    cloudCover: roundTo(isRainyDay ? getRandomNumber(0.7, 1.0) : getRandomNumber(0.1, 0.5), 2),
    maxUvIndex: isRainyDay ? getRandomInt(0, 3) : getRandomInt(4, 9),
    pollens: createWeatherReportPollen()
  };
}

// Function to create IWeatherReportCurrent data
function createWeatherReportCurrent(isRainy: boolean = false): IWeatherReportCurrent {
  const now = new Date();

  // Use values similar to what we'd generate in hourly data
  const temp = 33;
  const windSpeed = roundTo(getRandomNumber(5, 20), 1);
  const tempFeel = 32;

  const conditionCode: IConditionCode = isRainy
    ? (Math.random() < 0.3 ? 'Thunderstorms' : 'Rain')
    : (Math.random() < 0.5 ? 'Clear' : 'PartlyCloudy');

  const precipIntensity = isRainy ? getRandomNumber(1, 5) : 0;

  return {
    time: getMsSinceEpoch(now),
    temp,
    tempFeel,
    precipIntensity: roundTo(precipIntensity, 2),
    precipType: isRainy ? 'rain' : 'clear',
    conditionCode,
    windSpeed,
    windDirection: getRandomInt(0, 360),
    humidity: roundTo(isRainy ? getRandomNumber(0.7, 0.9) : getRandomNumber(0.4, 0.7), 2),
    pressure: getRandomInt(995, 1025),
    pressureTrend: Math.random() < 0.33 ? 'rising' : Math.random() < 0.5 ? 'falling' : 'steady',
    uvIndex: isRainy ? getRandomInt(0, 3) : getRandomInt(4, 9)
  };
}

// Function to create IWeatherReportWeek data
function createWeatherReportWeek(days: Omit<IWeatherReportDay, 'hours'>[]): IWeatherReportWeek {
  return { days };
}

// Function to create IGuestReport data
function createGuestReport(date: Date, isRainy: boolean = false): IGuestReport {
  // Use similar logic as the day report but simplified
  const temp = roundTo(isRainy ? getRandomNumber(12, 18) : getRandomNumber(15, 25), 1);
  const tempFeel = roundTo(temp - 1, 1);

  return {
    tempHigh: roundTo(temp + getRandomNumber(2, 5), 1),
    tempLow: roundTo(temp - getRandomNumber(3, 8), 1),
    precipChance: roundTo(isRainy ? getRandomNumber(0.6, 0.9) : getRandomNumber(0, 0.3), 2),
    precipType: isRainy ? 'rain' : 'clear',
    temp,
    tempFeel,
    dateNumber: generateDateNumber(date)
  };
}

// Main function to create the complete IWeatherReport
function createWeatherReport(): IWeatherReport {
  const now = new Date();
  const today = generateDateNumber(now);

  // Create days array with yesterday, today, and 5 more days
  const days: IWeatherReportDay[] = [];

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  days.push(createWeatherReportDay(yesterday, -1));

  // Today (rainy as requested)
  days.push(createWeatherReportDay(now, 0, true));

  // Next 5 days
  for (let i = 1; i <= 7; i++) {
    const futureDate = new Date(now);
    futureDate.setDate(futureDate.getDate() + i);
    days.push(createWeatherReportDay(futureDate, i));
  }

  // Create week data (omitting hours)
  const weekDays = days.map(({ hours, ...rest }) => rest);
  const week = createWeatherReportWeek(weekDays);

  return {
    id: `demo-report`,
    isDemo: true,
    locationTitle: "Norman, Oklahoma",
    today,
    timezone: "America/Chicago",
    current: createWeatherReportCurrent(true), // Current is rainy as requested
    days,
    week,
    nextHour: createWeatherReportNextHour(), // Next hour is rainy as requested
    reportTime: getMsSinceEpoch(now),
    receiptStatus: { expiresOnMs: Date.now() + (1000 * 60 * 24 * 24 * 365), statusMs: Date.now(), ok: true },
    isSubscribed: true,
    guestReport: createGuestReport(now, true)
  };
}

export const buildDemoWeatherReport = ():ILoadWeatherReportResponse => {
  return { report: createWeatherReport() };
}

